import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  model,
  output,
  signal,
  untracked,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { AbstractControl } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

import { Icon } from '../icon/icon';
import { Modal } from '../modal/modal';
import { ModalFooterDirective } from '../modal/modal-footer.directive';
import { ModalHeaderActionDirective } from '../modal/modal-header-action.directive';
import { ModalService } from '../modal/modal.service';
import { WizardStep } from './wizard-step';
import { WizardStepStatus, WizardStepView } from './models/wizard.model';

/** Rail circle classes per step status (DS: filled action dot when active,
 *  outlined circle while upcoming, status tints for done/error). */
const CIRCLE: Record<WizardStepStatus, string> = {
  active: 'bg-action text-action-text',
  done: 'bg-ok text-white',
  error: 'bg-danger-surface text-danger',
  upcoming: 'border border-strong text-ink-2',
};

/**
 * Multi-step wizard in a modal: progress bar, clickable step rail, one step
 * body at a time, Back/Next footer. Steps are declared as `app-wizard-step`
 * content children; wrap one in `@if` to add/remove it dynamically — rail,
 * numbering, and progress react, and navigation state survives (keyed by id).
 *
 *   <app-wizard-modal [(open)]="open" [title]="t('title')" (finished)="apply()">
 *     <app-wizard-step id="basics" [label]="t('basics')" [control]="basicsForm">
 *       <ng-template>…form fields…</ng-template>
 *     </app-wizard-step>
 *     @if (isBattery()) { <app-wizard-step id="power" … /> }
 *   </app-wizard-modal>
 *
 * Validation: Next stays enabled; a click on an invalid step marks its control
 * touched (surfacing the consumer's field errors) and stays put. Finishing
 * re-validates every step and jumps to the first invalid one. Consumers own
 * field-level messages (app-form-field); the wizard owns the rail error state
 * and the optional per-step `error` banner.
 *
 * Contract for `(finished)`: set `busy` while submitting (all navigation
 * no-ops) or close the wizard — otherwise a second click re-emits.
 */
@Component({
  selector: 'app-wizard-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    Icon,
    Modal,
    ModalFooterDirective,
    ModalHeaderActionDirective,
    NgTemplateOutlet,
    TranslocoDirective,
  ],
  templateUrl: './wizard-modal.html',
})
export class WizardModal {
  private readonly modalService = inject(ModalService);
  private readonly transloco = inject(TranslocoService);

  // ── Visibility (two-way) ─────────────────────────────────────────────
  readonly open = model.required<boolean>();

  // ── Chrome ───────────────────────────────────────────────────────────
  readonly title = input.required<string>();
  readonly subtitle = input('');
  /** Fixed card height (see `Modal.heightPx`) instead of the default
   *  content-driven sizing — use when a step's body can change size (e.g. an
   *  inline panel opening) and that should scroll in place rather than
   *  resize the dialog. null = existing behaviour (grows with content up to
   *  the shared 680px floor / 90vh cap). */
  readonly heightPx = input<number | null>(null);
  /** Last-step primary label; defaults to the translated `common.wizard.finish`. */
  readonly finishLabel = input<string | null>(null);
  /** Header text action ("Save & exit"); hidden when null. */
  readonly saveExitLabel = input<string | null>(null);

  // ── Behaviour ────────────────────────────────────────────────────────
  /** Disables all navigation while a consumer submit is in flight. */
  readonly busy = input(false);
  /** Ask before dismissing when any step's control is dirty. */
  readonly confirmClose = input(false);
  readonly dismissable = input(true);

  // ── Outputs ──────────────────────────────────────────────────────────
  readonly closed = output<void>();
  /** Last-step Next with every step valid. See the busy contract above. */
  readonly finished = output<void>();
  readonly saveExit = output<void>();
  /** Active step id, on every navigation. */
  readonly stepChanged = output<string>();

  // ── Steps ────────────────────────────────────────────────────────────
  readonly steps = contentChildren(WizardStep);

  private readonly activeId = signal<string | null>(null);
  /** Visited ids in first-visit order; never pruned, so a conditionally
   *  removed-then-restored step keeps its done/error status. */
  private readonly visitedIds = signal<readonly string[]>([]);
  /** Bumped on any bound control event (status/touched/dirty) — AbstractControl
   *  state isn't signal-based, so computeds below depend on this instead. */
  private readonly formsVersion = signal(0);
  /** Re-entrancy guard: a discard confirm is already stacked. */
  private confirming = false;

  protected readonly stepList = computed(() => this.steps());
  protected readonly activeIndex = computed(() =>
    this.stepList().findIndex((s) => s.id() === this.activeId()),
  );
  protected readonly activeStep = computed<WizardStep | null>(
    () => this.stepList()[this.activeIndex()] ?? null,
  );
  protected readonly isLast = computed(
    () => this.stepList().length > 0 && this.activeIndex() === this.stepList().length - 1,
  );
  protected readonly stepViews = computed<WizardStepView[]>(() => {
    this.formsVersion();
    const activeId = this.activeId();
    const activeIndex = this.activeIndex();
    const visited = this.visitedIds();
    const busy = this.busy();
    return this.stepList().map((step, index) => {
      const id = step.id();
      const isActive = id === activeId;
      const isVisited = visited.includes(id);
      const control = step.control();
      let status: WizardStepStatus = 'upcoming';
      if (isActive) status = 'active';
      // A step showing its error banner (e.g. a server rejection) is an error
      // in the rail too — a valid control must not green-check it away.
      else if (isVisited && step.error()) status = 'error';
      // A visited step holding invalid data must not get the green check. If
      // the user actually entered something (dirty) it is an error; a pristine
      // skipped-over step just falls back to the unvisited look (it stays
      // clickable, and finishing still routes to it).
      else if (isVisited && control && !control.valid)
        status = control.dirty ? 'error' : 'upcoming';
      else if (isVisited) status = 'done';
      // Visited steps and anything behind the active one are freely
      // revisitable; the immediate next step is clickable too and acts like
      // the Next button (gated in goTo).
      const clickable =
        !isActive && !busy && (isVisited || index < activeIndex || index === activeIndex + 1);
      return { step, index, status, clickable };
    });
  });

  constructor() {
    // Reset navigation on every open.
    effect(() => {
      if (!this.open()) return;
      untracked(() => {
        this.visitedIds.set([]);
        this.activeId.set(null);
        const first = this.stepList()[0];
        // Silent: landing on step 1 at open is initial state, not navigation.
        if (first) this.activate(first.id(), false);
      });
    });

    // Keep the active id valid as steps appear/disappear: first activation once
    // steps arrive, and remap to the last still-present visited step (else the
    // first step) when the active one is removed.
    effect(() => {
      const list = this.stepList();
      untracked(() => {
        const active = this.activeId();
        if (active !== null && list.some((s) => s.id() === active)) return;
        const fallback = [...this.visitedIds()]
          .reverse()
          .find((id) => list.some((s) => s.id() === id));
        const next = fallback ?? list[0]?.id() ?? null;
        // Silent when steps first arrive; a real remap (active step removed) emits.
        if (next !== null) this.activate(next, active !== null);
        else this.activeId.set(null);
      });
    });

    // Track bound-control state changes for the rail/dirty checks.
    effect((onCleanup) => {
      const subs = this.stepList()
        .map((s) => s.control())
        .filter((c): c is AbstractControl => c !== null)
        .map((c) => c.events.subscribe(() => this.formsVersion.update((v) => v + 1)));
      onCleanup(() => subs.forEach((s) => s.unsubscribe()));
    });
  }

  // ── Navigation ───────────────────────────────────────────────────────

  protected next(): void {
    if (this.busy()) return;
    const step = this.activeStep();
    if (!step) return;
    if (!this.leaveGatePasses(step)) return;
    if (!this.isLast()) {
      this.activate(this.stepList()[this.activeIndex() + 1].id());
      return;
    }
    // Finish: every step must hold valid data, visited or not.
    const invalid = this.stepList().find((s) => s.control() && !s.control()!.valid);
    if (invalid) {
      invalid.control()!.markAllAsTouched();
      this.activate(invalid.id());
      return;
    }
    this.finished.emit();
  }

  protected back(): void {
    if (this.busy()) return;
    const idx = this.activeIndex();
    if (idx <= 0) return;
    this.activate(this.stepList()[idx - 1].id());
  }

  /** Jump to a step (rail click / programmatic): visited steps and steps
   *  behind the active one freely (a conditional step can be inserted behind
   *  the current position unvisited), the immediate next step with the Next
   *  button's validation gate. False if not allowed (including a gate block,
   *  which surfaces the field errors). */
  goTo(id: string): boolean {
    if (this.busy() || id === this.activeId()) return false;
    const targetIndex = this.stepList().findIndex((s) => s.id() === id);
    if (targetIndex < 0) return false;
    if (this.visitedIds().includes(id) || targetIndex < this.activeIndex()) {
      this.activate(id);
      return true;
    }
    if (targetIndex !== this.activeIndex() + 1) return false;
    const step = this.activeStep();
    if (!step || !this.leaveGatePasses(step)) return false;
    this.activate(id);
    return true;
  }

  private activate(id: string, emit = true): void {
    if (this.activeId() === id) return;
    this.activeId.set(id);
    this.visitedIds.update((v) => (v.includes(id) ? v : [...v, id]));
    if (emit) this.stepChanged.emit(id);
  }

  /** Next is blocked (marking errors visible) while the step's data is bad. */
  private leaveGatePasses(step: WizardStep): boolean {
    const control = step.control();
    const canLeave = step.canLeave();
    if ((control && !control.valid) || (canLeave && !canLeave())) {
      control?.markAllAsTouched();
      return false;
    }
    return true;
  }

  // ── Close flow ───────────────────────────────────────────────────────

  /** Escape/backdrop/X when `confirmClose` is on: ask before discarding. */
  protected async onCloseRequested(): Promise<void> {
    if (this.confirming) return;
    const dirty = this.stepList().some((s) => s.control()?.dirty);
    if (!dirty) {
      this.forceClose();
      return;
    }
    this.confirming = true;
    try {
      const ok = await this.modalService.confirm({
        kind: 'warning',
        destructive: true,
        title: this.transloco.translate('common.wizard.discardTitle'),
        message: this.transloco.translate('common.wizard.discardMessage'),
        confirmLabel: this.transloco.translate('common.wizard.discardConfirm'),
      });
      if (ok) this.forceClose();
    } finally {
      this.confirming = false;
    }
  }

  private forceClose(): void {
    this.open.set(false);
    this.closed.emit();
  }

  // ── Template helpers ─────────────────────────────────────────────────

  protected circleCls(status: WizardStepStatus): string {
    return CIRCLE[status];
  }
}
